import React from 'react';
import VisaPageLayout, { FaqItem, ProcessStep, RequirementItem, VisaPlan } from './visa-page-layout';
import { useVisaPacksByCountry } from '../hooks/useVisaPacksByCountry';


const plans: VisaPlan[] = [
  { id: 'th-exempt', name: 'Visa Exemption — Assisted Entry Check', entryType: 'Single Entry', validity: 'Aligned to your travel dates', stay: 'Up to 60 days in Thailand', processing: 'Same day confirmation', price: '10', currency: 'OMR' },
  { id: 'th-tourist', name: 'Tourist eVisa', entryType: 'Single Entry', validity: 'Use within 90 days of issue', stay: 'Up to 60 days in Thailand', processing: '3–5 business days', price: '35', currency: 'OMR', featured: true },
  { id: 'th-multi', name: 'Tourist eVisa', entryType: 'Multiple Entry', validity: 'Valid for 6 months from issue', stay: 'Up to 60 days per visit', processing: '5–7 business days', price: '65', currency: 'OMR' },
];

const requirements: RequirementItem[] = [
  { label: 'Passport copy', detail: 'Valid for at least 6 months from your arrival date, with at least one blank page.' },
  { label: 'Passport-size photo', detail: 'Recent, plain white background, matching standard visa specifications.' },
  { label: 'Confirmed return ticket', detail: 'Round-trip or onward flight booking showing your departure from Thailand.' },
  { label: 'Hotel booking', detail: 'Confirmed accommodation covering your full length of stay.' },
  { label: 'Proof of funds', detail: 'A recent bank statement showing sufficient funds for your trip — commonly requested for Thai visa applications.' },
];

const goodToKnow: string[] = [
  'Many nationalities are eligible to enter Thailand visa-free for short tourist stays — we\'ll confirm whether you need to apply for anything at all before you pay for a service.',
  'The multiple-entry eVisa suits travelers combining Thailand with Cambodia, Laos or Vietnam on one trip.',
  'Overstaying a Thai visa carries daily fines and can affect future entry — we build a buffer into your recommended visa length.',
  'Some land border crossings have different rules to arrivals by air — tell us your route and we\'ll flag anything unusual.',
];

const steps: ProcessStep[] = [
  { title: 'Tell us your travel plan', text: 'Share your nationality, dates and entry point so we can confirm the right route.' },
  { title: 'We review & submit', text: 'For the eVisa route, our team checks your documents before filing with Thai immigration.' },
  { title: 'Track your status', text: 'We\'ll keep you updated by WhatsApp or email while your application is processed.' },
  { title: 'Travel with confidence', text: 'Arrive with your approved eVisa or entry confirmation already in hand.' },
];

const faqs: FaqItem[] = [
  { question: 'Do I actually need a visa for Thailand?', answer: 'It depends on your passport and length of stay — many travelers qualify for visa-free entry. Send us your nationality and trip length and we\'ll tell you honestly if you need to apply for anything.' },
  { question: 'How long can I stay in Thailand as a tourist?', answer: 'This varies by nationality and entry method, typically ranging from 30 to 60 days. We\'ll confirm the exact allowance for your situation.' },
  { question: 'Can I extend my stay once I\'m in Thailand?', answer: 'Extensions are possible through Thai immigration offices for an additional fee, though approval isn\'t guaranteed. We can advise on the process if your plans change.' },
  { question: 'Is the multiple-entry visa worth it for island hopping within Thailand?', answer: 'For travel within Thailand alone, a single-entry visa is usually sufficient — multiple entry is really for travelers crossing in and out of neighboring countries.' },
];

export default function VisaThailandPage() {
    const { plans, loading, error } = useVisaPacksByCountry('THA');
  return (
    <VisaPageLayout
      countryName="Thailand"
      flagEmoji="🇹🇭"
      heroEyebrow="Kingdom of Thailand"
      heroImage="https://images.unsplash.com/photo-1683641967671-196309483093?w=1600&q=80&fit=crop&crop=center"
      heroTitleLead="Your"
      heroTitleHighlight="Thailand"
      heroTitleTail="entry, confirmed before takeoff"
      heroSubtitle="From Phi Phi's limestone cliffs to Bangkok's temples — we confirm exactly what your Thailand trip needs, whether that's nothing at all or a fully prepared eVisa."
      trustStats={[
        { value: '3–7', label: 'Business Days' },
        { value: '97%', label: 'Approval Rate' },
        { value: '2,700+', label: 'Thailand Visas Issued' },
      ]}
      plans={plans}
      plansLoading={loading}
      plansError={error}
      requirements={requirements}
      goodToKnow={goodToKnow}
      steps={steps}
      faqs={faqs}
      ctaSubtitle="Tell us your nationality and travel dates — we'll confirm your entry requirements honestly, even if that means you don't need our visa service at all."
    />
  );
}